/** Pedidos de todos os canais: filtros por canal/conta, situação, período, envio e busca; resumo e detalhe. */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, Circle, CircleAlert, CircleCheck, Download, ExternalLink, History, ListChecks, LoaderCircle, MessageSquare, LifeBuoy, Printer, RefreshCw, Search, X } from 'lucide-react';
import type { ChannelInfo, OrderDetail, OrderRow, OrdersPage, OrdersSyncAccount, OrderStatus } from '@berytus/contracts';
import { api, brl, dateTime, int } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Loading, Tag, Thumb } from '../components/ui.tsx';
import { Head, Pager, useSearchBox } from './MeliChannel.tsx';
import { Modal } from './CatalogSettings.tsx';
import { can } from '@berytus/contracts';

const STATUS: Record<OrderStatus, { label: string; tone: string }> = {
  pending: { label: 'Aguardando pagamento', tone: 'off' },
  paid: { label: 'Pago', tone: 'info' },
  ready: { label: 'A enviar', tone: 'warn' },
  shipped: { label: 'Enviado', tone: 'info' },
  delivered: { label: 'Entregue', tone: 'ok' },
  cancelled: { label: 'Cancelado', tone: 'err' },
};
const LOGISTIC: Record<string, string> = {
  fulfillment: 'Full', self_service: 'Flex', cross_docking: 'Coleta', drop_off: 'Agência', xd_drop_off: 'Agência',
  shopee: 'Shopee Envios', ifood_delivery: 'Entrega iFood', own_delivery: 'Entrega própria', takeout: 'Retirada', indoor: 'No local',
};
const CHANNEL: Record<string, string> = { mercadolivre: 'Mercado Livre', shopee: 'Shopee', ifood: 'iFood' };
/** Etapas do envio que importam no Instant (entregador). */
const SUBSTATUS: Record<string, string> = {
  ready_to_print: 'etiqueta a imprimir', printed: 'etiqueta impressa', looking_for_driver: 'procurando entregador',
  rider_not_found: 'sem entregador', on_route_to_pickup: 'entregador a caminho da loja', picking_up: 'entregador na loja',
  picked_up: 'coletado', out_for_delivery: 'saiu para entrega', soon_deliver: 'entrega em breve', at_the_door: 'na porta do comprador',
  receiver_absent: 'comprador ausente', delivery_failed: 'entrega falhou', returning_to_sender: 'voltando para a loja',
};
const SHIPMENT: Record<string, string> = {
  pending: 'pendente', handling: 'em preparação', ready_to_ship: 'pronto para envio', shipped: 'a caminho',
  delivered: 'entregue', not_delivered: 'não entregue', cancelled: 'cancelado',
};
const CHANNEL_STATUS: Record<string, string> = {
  paid: 'pago', cancelled: 'cancelado', confirmed: 'confirmado', payment_required: 'aguardando pagamento',
  payment_in_process: 'pagamento em processamento', partially_paid: 'pago em parte', partially_refunded: 'reembolso parcial', invalid: 'inválido',
  // Shopee
  UNPAID: 'aguardando pagamento', READY_TO_SHIP: 'pronto para enviar', INVOICE_PENDING: 'aguardando nota fiscal', PROCESSED: 'processado',
  RETRY_SHIP: 'reenviar', SHIPPED: 'enviado', TO_CONFIRM_RECEIVE: 'aguardando confirmação de entrega', COMPLETED: 'concluído',
  IN_CANCEL: 'cancelamento solicitado', CANCELLED: 'cancelado', TO_RETURN: 'devolução',
  // iFood
  PLACED: 'novo (aguardando confirmação)', CONFIRMED: 'confirmado', SEPARATION_STARTED: 'em separação', SEPARATION_ENDED: 'separado',
  READY_TO_PICKUP: 'pronto para retirada', DISPATCHED: 'saiu para entrega', CONCLUDED: 'concluído',
};
const shortDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');
const shortTime = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const PERIODS: [string, string, () => [string | null, string | null]][] = [
  ['today', 'Hoje', () => [ymd(new Date()), ymd(new Date())]],
  ['7d', '7 dias', () => [ymd(new Date(Date.now() - 6 * 86400_000)), null]],
  ['30d', '30 dias', () => [ymd(new Date(Date.now() - 29 * 86400_000)), null]],
  ['month', 'Este mês', () => { const n = new Date(); return [ymd(new Date(n.getFullYear(), n.getMonth(), 1)), null]; }],
  ['year', 'Este ano', () => [`${new Date().getFullYear()}-01-01`, null]],
  ['all', 'Tudo', () => [null, null]],
];

const COLS: [string, number, string?][] = [
  ['Data', 120], ['Pedido', 150], ['Produto', 0], ['Un.', 60, 'num'], ['Total', 110, 'num'], ['Comissão', 100, 'num'], ['Situação', 150], ['Envio', 150],
];

export function OrdersPage() {
  const me = useMe();
  const admin = can(me.tenant.access, 'orders', 'write') && !me.impersonating;
  const [params, setParams] = useSearchParams();
  const set = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(p)) (v == null || v === '' ? next.delete(k) : next.set(k, v));
    setParams(next, { replace: true });
  };
  const [search, setSearch] = useSearchBox(params, set);
  const period = params.get('period') ?? (params.get('from') || params.get('to') ? 'custom' : 'today');
  const [from, to] = period === 'custom' ? [params.get('from'), params.get('to')] : (PERIODS.find((p) => p[0] === period) ?? PERIODS[0]!)[2]();
  const page = Math.max(1, Number(params.get('page') ?? 1));
  const [open, setOpen] = useState<string | null>(null);

  const qs = new URLSearchParams();
  for (const k of ['q', 'status', 'account', 'channel', 'logistic', 'unlinked', 'integration', 'alert', 'buyer', 'sort', 'dir']) if (params.get(k)) qs.set(k, params.get(k)!);
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  qs.set('page', String(page));
  qs.set('pageSize', '50');
  const list = useQuery({ queryKey: ['orders', qs.toString()], queryFn: () => api<OrdersPage>(`/orders?${qs}`), placeholderData: keepPreviousData });
  const sync = useQuery({
    queryKey: ['orders-sync'],
    queryFn: () => api<OrdersSyncAccount[]>('/orders/sync'),
    refetchInterval: (q) => (q.state.data?.some((a) => a.backfill && ['queued', 'running'].includes(a.backfill.status)) ? 5000 : 60_000),
  });
  const channels = useQuery({ queryKey: ['channels'], queryFn: () => api<ChannelInfo[]>('/channels') });
  const accounts = channels.data?.flatMap((c) => c.accounts.filter((a) => a.status !== 'revoked').map((a) => ({ ...a, channel: c.code, channelName: c.name }))) ?? [];
  const sum = list.data?.summary;

  return (
    <>
      <Head title="Pedidos">
        <p>Pedidos de todos os canais conectados, com filtros por canal, situação e período. Os pedidos chegam sozinhos a cada poucos minutos.</p>
      </Head>

      <SyncBar accounts={sync.data ?? []} admin={can(me.tenant.access, 'settings', 'write') && !me.impersonating} />
      {!!sum?.packGrew && params.get('alert') !== 'pack_grew' && (
        <div className="notice err small" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <span><b>{int(sum.packGrew)} carrinho{sum.packGrew === 1 ? '' : 's'}</b> ganhou pedido depois que o ERP já tinha recebido: o pedido no ERP está sem esses itens.</span>
          <button type="button" className="btn sm" onClick={() => set({ alert: 'pack_grew', page: null })}>Ver os carrinhos</button>
        </div>
      )}
      {!!sum?.cnpjManual && params.get('alert') !== 'cnpj' && (
        <div className="notice warn small" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <span><b>{int(sum.cnpjManual)} pedido{sum.cnpjManual === 1 ? '' : 's'} para CNPJ</b> {sum.cnpjManual === 1 ? 'precisa' : 'precisam'} ser cancelado{sum.cnpjManual === 1 ? '' : 's'} à mão no canal (a empresa não vende para CNPJ). {sum.cnpjManual === 1 ? 'Ele não foi' : 'Eles não foram'} para o ERP.</span>
          <button type="button" className="btn sm" onClick={() => set({ alert: 'cnpj', page: null })}>Ver os pedidos</button>
        </div>
      )}
      {!!sum?.noFinance && params.get('alert') !== 'no_finance' && (
        <div className="notice warn small" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <span><b>{int(sum.noFinance)} pedido{sum.noFinance === 1 ? '' : 's'} pago{sum.noFinance === 1 ? '' : 's'}</b> sem financeiro há mais de 30 minutos (comissão, taxas e líquido). A Berytus tenta de novo a cada 10 minutos.</span>
          <button type="button" className="btn sm" onClick={() => set({ alert: 'no_finance', page: null })}>Ver os pedidos</button>
        </div>
      )}
      {params.get('alert') === 'no_finance' && (
        <div className="notice small" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <span>Pedidos pagos dos últimos 7 dias ainda sem financeiro. Abra o pedido para ver o motivo da última tentativa.</span>
          <button type="button" className="btn sm" onClick={() => set({ alert: null, page: null })}><X size={13} /> Tirar o filtro</button>
        </div>
      )}
      {params.get('alert') === 'cnpj' && (
        <div className="notice small" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <span>Pedidos para CNPJ segurados (a empresa não vende para CNPJ), em qualquer data. Abra o pedido para ver o que a Berytus fez.</span>
          <button type="button" className="btn sm" onClick={() => set({ alert: null, page: null })}><X size={13} /> Tirar o filtro</button>
        </div>
      )}
      {params.get('alert') === 'at_risk' && (
        <div className="notice warn small" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <span>Pedidos em risco de atraso, em qualquer data: prazo de despacho em até 12 h (ou vencido), sem etiqueta ou sem nota.</span>
          <button type="button" className="btn sm" onClick={() => set({ alert: null, page: null })}><X size={13} /> Tirar o filtro</button>
        </div>
      )}
      {params.get('alert') === 'pack_grew' && (
        <div className="notice small" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <span>Carrinhos que ganharam pedido depois do ERP (em qualquer data). Abra o carrinho para ver os itens que entraram depois.</span>
          <button type="button" className="btn sm" onClick={() => set({ alert: null, page: null })}><X size={13} /> Tirar o filtro</button>
        </div>
      )}

      <div className="toolbar">
        <label className="search">
          <Search size={16} />
          <span className="sr-only">Buscar</span>
          <input className="input" type="search" placeholder="Nº do pedido ou do carrinho, SKU, produto ou comprador" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        {accounts.length > 1 && (
          <select className="select" aria-label="Canal" value={params.get('account') ? `acc:${params.get('account')}` : params.get('channel') ? `ch:${params.get('channel')}` : ''}
            onChange={(e) => {
              const v = e.target.value;
              set({ account: v.startsWith('acc:') ? v.slice(4) : null, channel: v.startsWith('ch:') ? v.slice(3) : null, page: null });
            }}>
            <option value="">Todos os canais</option>
            {[...new Set(accounts.map((a) => a.channel))].map((ch) => {
              const list = accounts.filter((a) => a.channel === ch);
              const name = list[0]!.channelName;
              return list.length > 1 ? (
                <optgroup key={ch} label={name}>
                  <option value={`ch:${ch}`}>{name}: todas as contas</option>
                  {list.map((a) => <option key={a.id} value={`acc:${a.id}`}>{a.displayName ?? a.externalAccountId}</option>)}
                </optgroup>
              ) : <option key={ch} value={`ch:${ch}`}>{name}</option>;
            })}
          </select>
        )}
        <select className="select" aria-label="Situação" value={params.get('status') ?? 'all'} onChange={(e) => set({ status: e.target.value === 'all' ? null : e.target.value, page: null })}>
          <option value="all">Qualquer situação</option>
          <option value="open">Em aberto (pagos e a enviar)</option>
          {(Object.keys(STATUS) as OrderStatus[]).map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
        </select>
        <select className="select" aria-label="Envio" value={params.get('logistic') ?? 'all'} onChange={(e) => set({ logistic: e.target.value === 'all' ? null : e.target.value, page: null })}>
          <option value="all">Qualquer envio</option>
          <option value="instant">Instant (entrega em minutos)</option>
          <option value="fulfillment">Full (Mercado Livre e Shopee)</option>
          <option value="self_service">Flex</option>
          <option value="cross_docking">Coleta</option>
          <option value="drop_off">Agência</option>
          <option value="to_agree">A combinar com o vendedor (sem Mercado Envios)</option>
          <option value="shopee">Shopee: envio pela Shopee</option>
          <option value="shopee_turbo">Shopee: Entrega Turbo</option>
          <option value="shopee_fast">Shopee: Entrega Rápida</option>
          <option value="shopee_full">Shopee: Full da Shopee</option>
          <option value="other">Outros / sem envio</option>
        </select>
        <select className="select" aria-label="Integração" value={params.get('integration') ?? 'all'} onChange={(e) => set({ integration: e.target.value === 'all' ? null : e.target.value, page: null })}>
          <option value="all">Qualquer integração</option>
          <option value="pending">Aguardando o ERP buscar</option>
          <option value="acked">Recebidos pelo ERP</option>
          <option value="no_invoice">Sem nota fiscal</option>
          <option value="invoice_error">Nota com erro</option>
        </select>
        {/* Etiqueta ainda não emitida: envio pronto para despachar e etiqueta não baixada pela Berytus (ML e Shopee). */}
        <select className="select" aria-label="Etiqueta" value={params.get('alert') === 'unprinted' ? 'unprinted' : 'all'}
          onChange={(e) => set({ alert: e.target.value === 'unprinted' ? 'unprinted' : params.get('alert') === 'unprinted' ? null : params.get('alert'), page: null })}>
          <option value="all">Qualquer etiqueta</option>
          <option value="unprinted">Etiqueta ainda não emitida</option>
        </select>
        <select className="select" aria-label="Comprador" value={params.get('buyer') ?? ''} onChange={(e) => set({ buyer: e.target.value || null, page: null })}>
          <option value="">Comprador: CPF e CNPJ</option>
          <option value="cnpj">Só CNPJ (pessoa jurídica)</option>
          <option value="cpf">Só CPF (pessoa física)</option>
        </select>
        {admin && params.get('integration') === 'invoice_error' && <RetryInvoices />}
        <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" className="check" checked={params.get('unlinked') === 'true'} onChange={(e) => set({ unlinked: e.target.checked ? 'true' : null, page: null })} />
          Com item sem produto
        </label>
      </div>
      <div className="toolbar" style={{ marginTop: -4 }}>
        <div className="seg" role="group" aria-label="Período">
          {PERIODS.map(([k, l]) => (
            <button key={k} type="button" aria-pressed={period === k} onClick={() => set({ period: k === 'today' ? null : k, from: null, to: null, page: null })}>{l}</button>
          ))}
        </div>
        <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          De <input className="input" type="date" style={{ width: 150 }} value={from ?? ''} onChange={(e) => set({ period: 'custom', from: e.target.value || null, to: to ?? null, page: null })} />
        </label>
        <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          até <input className="input" type="date" style={{ width: 150 }} value={to ?? ''} onChange={(e) => set({ period: 'custom', from: from ?? null, to: e.target.value || null, page: null })} />
        </label>
        <a className="btn" style={{ marginLeft: 'auto' }} title="Baixa os pedidos deste filtro e período em planilha (abre no Excel), uma linha por item"
          href={`/api/v1/orders/export?${(() => { const e = new URLSearchParams(qs); e.delete('page'); e.delete('pageSize'); return e; })()}`} download>
          <Download size={15} /> Exportar para Excel
        </a>
      </div>

      <div className="kpis" style={{ marginBottom: 14, opacity: list.isPlaceholderData ? 0.55 : 1 }}>
        <div className="kpi"><span className="k-label">Pedidos</span><span className="k-value">{sum ? int(sum.orders) : '—'}</span><span className="k-note">{sum ? `${int(sum.cancelled)} cancelados` : ' '}</span></div>
        <div className="kpi"><span className="k-label">Faturamento</span><span className="k-value">{sum ? brl(sum.revenue) : '—'}</span><span className="k-note">sem os cancelados</span></div>
        <div className="kpi"><span className="k-label">Unidades vendidas</span><span className="k-value">{sum ? int(sum.units) : '—'}</span><span className="k-note">{sum && sum.orders - sum.cancelled > 0 ? `ticket médio ${brl(sum.revenue / (sum.orders - sum.cancelled))}` : ' '}</span></div>
        <div className="kpi"><span className="k-label">Comissões do canal</span><span className="k-value">{sum ? brl(sum.fees) : '—'}</span><span className="k-note">{sum && sum.revenue > 0 ? `${((100 * sum.fees) / sum.revenue).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% do faturamento` : ' '}</span></div>
        {!!sum?.unprinted && (
          <button type="button" className={`kpi${params.get('alert') === 'unprinted' ? ' active' : ''}`} style={{ textAlign: 'left', cursor: 'pointer' }}
            onClick={() => set({ alert: params.get('alert') === 'unprinted' ? null : 'unprinted', page: null })}>
            <span className="k-label"><Printer size={13} style={{ verticalAlign: -2 }} /> Etiquetas não impressas</span>
            <span className="k-value" style={{ color: 'var(--warn)' }}>{int(sum.unprinted)}</span>
            <span className="k-note">prontos para envio (Mercado Livre e Shopee) · clique para ver</span>
          </button>
        )}
        {sum && sum.unlinked > 0 && (
          <button type="button" className="kpi alert" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => set({ unlinked: 'true', page: null })}>
            <span className="k-label">Com item sem produto</span><span className="k-value">{int(sum.unlinked)}</span><span className="k-note">vincule o anúncio para o estoque baixar</span>
          </button>
        )}
      </div>

      <div className="panel flush">
        {list.error ? <div style={{ padding: 18 }}><ErrorBox error={list.error} /></div>
          : !list.isPending && list.data!.items.length === 0 ? (
            <div className="empty"><b>Nenhum pedido</b>{(sync.data?.length ?? 0) === 0 ? 'Conecte um canal para os pedidos aparecerem aqui.' : 'Mude o período ou os filtros.'}</div>
          ) : (
            <>
              <div className="grid-wrap" style={{ opacity: list.isPlaceholderData ? 0.55 : 1 }}>
                <table className="grid" style={{ width: '100%', minWidth: 980 }}>
                  <colgroup>{COLS.map(([, w], i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
                  <thead><tr>{COLS.map(([l, , c], i) => <th key={i} className={c}>{l}</th>)}</tr></thead>
                  <tbody>
                    {list.isPending
                      ? Array.from({ length: 10 }, (_, i) => <tr key={i}>{COLS.map((_, j) => <td key={j}><div className="skeleton" /></td>)}</tr>)
                      : list.data!.items.map((o) => <Row key={o.id} o={o} onOpen={() => setOpen(o.id)} />)}
                  </tbody>
                </table>
              </div>
              {list.data && <Pager total={list.data.total} page={page} pageSize={50} onPage={(p) => set({ page: p > 1 ? String(p) : null })} />}
            </>
          )}
      </div>
      <OrderDialog id={open} onClose={() => setOpen(null)} />
    </>
  );
}

function Row({ o, onOpen }: { o: OrderRow; onOpen: () => void }) {
  return (
    <tr onClick={onOpen} style={{ cursor: 'pointer', ...(o.isInstant ? { background: 'var(--ok-bg)' } : {}) }} title={o.isInstant ? 'Instant: entrega em minutos' : undefined}>
      <td><div className="cell-2"><span className="tnum">{shortDate(o.createdAt)}</span><span className="sub tnum">{shortTime(o.createdAt)}</span></div></td>
      <td>
        <div className="cell-2">
          <span className="code">{o.displayId ?? o.externalId}</span>
          <span className="sub">{CHANNEL[o.channel] ?? o.channel}{o.storeName && o.storeName !== 'Loja principal' && ` · ${o.storeName}`}{o.packId && ((o.ordersCount ?? 1) > 1 ? ` · carrinho com ${o.ordersCount} pedidos` : ' · carrinho')}</span>
        </div>
      </td>
      <td>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
          <Thumb src={o.firstItem?.thumbnail ?? null} alt="" />
          <div className="cell-2" style={{ minWidth: 0 }}>
            <span title={o.firstItem?.title ?? undefined} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {o.firstItem?.title ?? '—'}{o.itemsCount > 1 && <span className="muted"> +{o.itemsCount - 1}</span>}
            </span>
            <span className="sub">
              {o.firstItem?.sku ? `SKU ${o.firstItem.sku}` : ''}{o.buyerNickname && `${o.firstItem?.sku ? ' · ' : ''}${o.buyerNickname}`}
              {o.hasUnlinked && <> · <span style={{ color: 'var(--err)' }}>item sem produto</span></>}
            </span>
          </div>
        </div>
      </td>
      <td className="num">{int(o.units)}</td>
      <td className="num">{brl(o.totalAmount)}</td>
      <td className="num muted">{brl(o.saleFee)}</td>
      <td>
        <div className="cell-2">
          <span>
            <Tag tone={STATUS[o.status].tone}>{STATUS[o.status].label}</Tag>
            {o.packGrewAt && <> <Tag tone="err">carrinho cresceu</Tag></>}
            {o.buyerDocType === 'CNPJ' && !o.cnpjHold && <> <Tag tone="info">CNPJ</Tag></>}
            {o.cnpjHold && <> <Tag tone={o.cnpjHold.action === 'cancelled' ? 'off' : 'warn'}>{o.cnpjHold.action === 'cancelled' ? 'CNPJ: cancelado' : 'CNPJ: cancelar à mão'}</Tag></>}
            {/* Cancelado por receita recusada: RECEITA RECUSADA. Pedido cancelado não tem mais receita pendente. */}
            {o.prescription && !(o.status === 'cancelled' && o.prescription === 'WAITING_VALIDATION') && (
              <> <Tag tone={o.prescription === 'WAITING_VALIDATION' ? 'warn' : o.prescription === 'ACCEPTED' ? 'ok' : 'err'}>
                {o.prescription === 'WAITING_VALIDATION' ? 'receita pendente' : o.prescription === 'ACCEPTED' ? 'receita aceita' : o.status === 'cancelled' ? 'RECEITA RECUSADA' : 'receita recusada'}
              </Tag></>
            )}
          </span>
          {o.integrationStatus !== 'none' && (
            <span className="sub">
              {o.invoiceStatus === 'error' ? <span style={{ color: 'var(--err)' }}>nota com erro</span>
                : o.invoiceStatus === 'sent' ? 'nota enviada'
                : o.integrationStatus === 'acked' ? 'no ERP' : 'aguardando ERP'}
            </span>
          )}
        </div>
      </td>
      <td>
        {o.isInstant ? <Tag tone="ok">Instant</Tag> : o.logisticType ? (o.channel === 'shopee' && o.logisticType === 'fulfillment' ? 'Full Shopee' : LOGISTIC[o.logisticType] ?? o.logisticType) : <span className="muted">—</span>}
        {!o.isInstant && o.shippingService && <div className="small muted" title={o.shippingService} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.shippingService}</div>}
        {o.isInstant && o.shipmentSubstatus && <div className="small muted">{SUBSTATUS[o.shipmentSubstatus] ?? o.shipmentSubstatus.replaceAll('_', ' ')}</div>}
      </td>
    </tr>
  );
}

function SyncBar({ accounts, admin }: { accounts: OrdersSyncAccount[]; admin: boolean }) {
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: (accountId: string) => api('/orders/import', { method: 'POST', json: { accountId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders-sync'] }),
  });
  const [refreshed, setRefreshed] = useState<string | null>(null);
  const refresh = useMutation({
    mutationFn: (accountId: string) => api('/orders/refresh', { method: 'POST', json: { accountId } }),
    onSuccess: (_d, accountId) => {
      setRefreshed(accountId);
      // A leitura roda no worker: recarrega a lista em alguns segundos.
      setTimeout(() => { setRefreshed(null); void qc.invalidateQueries({ queryKey: ['orders'] }); void qc.invalidateQueries({ queryKey: ['orders-sync'] }); }, 8000);
    },
  });
  if (!accounts.length) return null;
  return (
    <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
      {accounts.map((a) => {
        const b = a.backfill;
        const running = b && ['queued', 'running'].includes(b.status);
        const st = (b?.stats ?? {}) as { month?: string; orders?: number };
        return (
          <div key={a.accountId} className="notice small" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>
              <b>{CHANNEL[a.channel] ?? a.channel}{a.name ? ` · ${a.name}` : ''}</b> · {int(a.orders)} pedidos
              {a.oldest && <> desde {new Date(a.oldest).toLocaleDateString('pt-BR')}</>}
              {a.syncedAt && <> · atualizado {dateTime(a.syncedAt)}</>}
              {running && <> · <b>lendo o histórico</b>{st.month ? ` (${st.month}, ${int(st.orders ?? 0)} lidos)` : '…'}</>}
              {b?.status === 'failed' && <> · <span style={{ color: 'var(--err)' }}>a leitura do histórico falhou: {b.error}</span></>}
            </span>
            <span style={{ flex: 1 }} />
            {a.liveFrom && <span className="muted">pedidos a partir de {new Date(a.liveFrom).toLocaleDateString('pt-BR')} mexem no estoque</span>}
            {admin && (a.channel === 'mercadolivre' || a.channel === 'shopee') && (
              <button className="btn sm" type="button" disabled={refresh.isPending || refreshed === a.accountId} onClick={() => refresh.mutate(a.accountId)}
                title="Lê agora os pedidos novos e alterados, sem esperar a leitura de rotina">
                <RefreshCw size={14} /> {refreshed === a.accountId || (refresh.isPending && refresh.variables === a.accountId) ? 'Atualizando…' : 'Atualizar pedidos'}
              </button>
            )}
            {admin && !running && (
              <button className="btn sm" type="button" disabled={run.isPending} onClick={() => run.mutate(a.accountId)}>
                <History size={14} /> {a.channel === 'shopee' ? 'Ler os últimos 90 dias' : a.orders ? 'Ler o histórico de novo' : 'Ler o histórico'}
              </button>
            )}
          </div>
        );
      })}
      {(run.error || refresh.error) && <ErrorBox error={run.error ?? refresh.error} />}
    </div>
  );
}

const EFFECT: Record<OrderRow['stockEffect'], string> = {
  deduct: 'A Berytus baixou do estoque da loja (e devolve se o pedido for cancelado).',
  reserve: 'Reservado nos canais até o seu sistema mandar o estoque atualizado.',
  none: 'Não mexe no estoque: pedido do histórico, do Full ou com a opção de estoque desligada.',
};

export function OrderDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const q = useQuery({ queryKey: ['order', id], queryFn: () => api<OrderDetail>(`/orders/${id}`), enabled: !!id });
  const o = q.data;
  return (
    <Dialog.Root open={!!id} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" aria-describedby={undefined}
          style={{ position: 'fixed', top: '5vh', left: '50%', transform: 'translateX(-50%)', width: 'min(780px, calc(100vw - 32px))', maxHeight: '90vh', overflow: 'auto', zIndex: 60, display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Dialog.Title asChild><h2 style={{ margin: 0 }}>{o?.packOrders ? `Carrinho ${o.packId}` : `Pedido ${o?.displayId ?? o?.externalId ?? ''}`}</h2></Dialog.Title>
            {o && <Tag tone={STATUS[o.status].tone}>{STATUS[o.status].label}</Tag>}
            {o?.status === 'cancelled' && o.prescription === 'REJECTED' && <Tag tone="err">RECEITA RECUSADA</Tag>}
            <span style={{ flex: 1 }} />
            {o?.channel === 'mercadolivre' && <Link className="btn sm" to={`/canal/mercadolivre/mensagens?order=${o.id}`}><MessageSquare size={13} /> Mensagens</Link>}
            {o?.channel === 'mercadolivre' && <Link className="btn sm" to={`/canal/mercadolivre/reclamacoes?order=${o.id}`}><LifeBuoy size={13} /> Reclamações</Link>}
            {o?.permalink && <a className="btn sm" href={o.permalink} target="_blank" rel="noreferrer">Ver no Mercado Livre <ExternalLink size={13} /></a>}
            <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
          </div>
          {q.isPending ? <Loading /> : q.error ? <ErrorBox error={q.error} /> : o && (
            <>
              {o.status === 'cancelled' && o.prescription === 'REJECTED' && (
                <div className="notice warn small"><b>Pedido cancelado: RECEITA RECUSADA.</b> O farmacêutico recusou a receita do medicamento e o pedido foi cancelado no Mercado Livre.</div>
              )}
              <dl className="facts">
                <div><dt>Data</dt><dd>{dateTime(o.createdAt)}</dd></div>
                <div><dt>Canal</dt><dd>{CHANNEL[o.channel] ?? o.channel}{o.accountName && ` · ${o.accountName}`}</dd></div>
                <div><dt>Loja (estoque)</dt><dd>{o.storeName ?? '—'}</dd></div>
                <div><dt>Comprador</dt><dd>{o.buyerNickname ? <>{o.buyerNickname}{o.buyerName && ` (${o.buyerName})`}</> : (o.buyerName ?? '—')}</dd></div>
                <div><dt>Total</dt><dd>{brl(o.totalAmount)}</dd></div>
                <div><dt>Comissão e taxas do canal</dt><dd>{o.saleFee != null ? brl(o.saleFee) : o.channel === 'shopee' ? <span className="muted">aguardando extrato</span> : '—'}</dd></div>
                <div><dt>Carrinho</dt><dd>{o.packId ?? '—'}{o.packOrders && (
                  <div className="small muted">{o.packOrders.length} pedidos: {o.packOrders.map((p) => `${p.externalId} (${brl(p.totalAmount)})${p.lateInPack ? ' — entrou depois do ERP' : ''}`).join(' · ')}</div>
                )}</dd></div>
                <div><dt>Envio</dt><dd>{o.logisticType ? LOGISTIC[o.logisticType] ?? o.logisticType : '—'}{o.shipmentStatus && ` · ${SHIPMENT[o.shipmentStatus] ?? o.shipmentStatus}`}</dd></div>
                <div><dt>Rastreio</dt><dd>{o.trackingNumber ?? '—'}</dd></div>
                <div><dt>Enviado / entregue</dt><dd>{o.shippedAt ? dateTime(o.shippedAt) : '—'} / {o.deliveredAt ? dateTime(o.deliveredAt) : '—'}</dd></div>
                <div><dt>Prazo para despachar</dt><dd>{o.handlingLimit ? dateTime(o.handlingLimit) : '—'}</dd></div>
                <div><dt>Previsão de entrega</dt><dd>{o.estimatedDelivery ? new Date(o.estimatedDelivery).toLocaleDateString('pt-BR') : '—'}{o.shippingService && ` · ${o.shippingService}`}</dd></div>
                <div><dt>Frete pago pelo comprador</dt><dd>{brl(o.shippingPaidByBuyer)}{o.discountAmount ? ` · desconto ${brl(o.discountAmount)}` : ''}</dd></div>
                <div><dt>Situação no canal</dt><dd>{o.channelStatus ? CHANNEL_STATUS[o.channelStatus] ?? o.channelStatus : '—'}{o.statusDetail && ` · ${o.statusDetail}`}</dd></div>
              </dl>
              {o.packGrewAt && <PackGrew o={o} />}
              {o.cnpjHold && <CnpjHold o={o} />}
              {o.finance ? <FinancePanel f={o.finance} />
                : o.status !== 'cancelled' && o.status !== 'pending' && o.integrationStatus !== 'none' && (
                  <div className="notice small">
                    <b>Financeiro ainda não calculado</b> (comissão, taxas e líquido). {o.financeError ? <>Última tentativa: {o.financeError}. </> : ''}
                    A Berytus calcula quando o pedido chega e tenta de novo a cada 10 minutos.
                  </div>
                )}
              {o.channel === 'ifood' ? <IfoodPanel o={o} /> : <Fulfillment o={o} />}
              {o.meliCancel && <MeliCancel o={o} />}
              <div className="notice small">{EFFECT[o.stockEffect]}</div>
              <table className="simple">
                <thead><tr><th>Item</th><th>Produto na Berytus</th><th className="num">Qtd.</th><th className="num">Preço</th><th className="num">Comissão</th><th>Estoque</th></tr></thead>
                <tbody>
                  {o.items.map((i) => (
                    <tr key={i.id}>
                      <td>
                        <div>{i.title}</div>
                        <div className="small muted">{i.listingId}{i.variationId && ` · var. ${i.variationId}`}{i.sellerSku && ` · SKU ${i.sellerSku}`}{o.packOrders && i.orderExternalId && ` · pedido ${i.orderExternalId}`}</div>
                        {i.lateInPack && <Tag tone="err">entrou depois do ERP — não está no pedido do ERP</Tag>}
                      </td>
                      <td>
                        {i.product ? <Link to={`/catalogo/${i.product.id}`}>{i.product.sku}</Link> : <span style={{ color: 'var(--err)' }}>sem vínculo</span>}
                        {i.product?.isKit && <> <Tag tone="info">kit</Tag></>}
                        {i.baseUnits != null && i.baseUnits !== i.quantity && <div className="small muted">{int(i.baseUnits)} un. do produto base</div>}
                      </td>
                      <td className="num">{int(i.quantity)}</td>
                      <td className="num">{brl(i.unitPrice)}</td>
                      <td className="num">{brl(i.saleFee != null ? i.saleFee * i.quantity : null)}</td>
                      <td className="small">
                        {i.stockMoves.length ? i.stockMoves.map((m, k) => <div key={k}>{m.kind === 'sale' ? 'baixa' : 'devolução'} {m.quantity > 0 ? '+' : ''}{m.quantity}</div>) : <span className="muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {o.items.some((i) => !i.product) && (
                <p className="small muted" style={{ margin: 0 }}>Item sem vínculo: vincule o anúncio a um produto na tela de anúncios do canal; os pedidos passam a apontar para ele.</p>
              )}
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Reprocessar as notas que não chegaram ao canal (com erro, ou recebidas e não enviadas), em lote. */
function RetryInvoices() {
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: () => api<{ total: number; sent: number; failed: number; errors: { orderId: string; error: string }[] }>('/orders/invoices/retry-errors', { method: 'POST' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['orders'] }); },
  });
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <button className="btn" type="button" disabled={run.isPending} onClick={() => run.mutate()}
        title="Reenvia ao canal as notas com erro e as recebidas que ainda não foram enviadas (pedidos pagos dos últimos 30 dias)">
        <RefreshCw size={15} /> {run.isPending ? 'Reprocessando…' : 'Reprocessar notas com erro'}
      </button>
      {run.data && (
        <span className="small" title={run.data.errors.map((e) => e.error).join('\n')}>
          {run.data.total === 0 ? 'Nenhuma nota para reenviar.' : <><b>{int(run.data.sent)}</b> enviada(s){run.data.failed > 0 && <> · <span style={{ color: 'var(--err)' }}>{int(run.data.failed)} com erro</span></>}</>}
        </span>
      )}
      {run.error && <ErrorBox error={run.error} />}
    </span>
  );
}

/** Pedido para CNPJ segurado (a empresa não vende para CNPJ): o que a Berytus fez e, se preciso, marcar como resolvido. */
function CnpjHold({ o }: { o: OrderDetail }) {
  const me = useMe();
  const admin = can(me.tenant.access, 'orders', 'write') && !me.impersonating;
  const qc = useQueryClient();
  const h = o.cnpjHold!;
  const ok = useMutation({
    mutationFn: () => api(`/orders/${o.id}/cnpj-ok`, { method: 'POST' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['order', o.id] }); void qc.invalidateQueries({ queryKey: ['orders'] }); },
  });
  const where = o.channel === 'shopee' ? 'na Shopee' : 'no Mercado Livre';
  return (
    <div className={`notice small ${h.action === 'cancelled' ? '' : 'warn'}`} style={{ display: 'grid', gap: 6 }}>
      <b>Pedido para CNPJ: a empresa não vende para pessoa jurídica. Ele não foi para o ERP.</b>
      <span>
        {h.action === 'cancelled' ? `A Berytus cancelou ${where} em ${dateTime(h.at)} (venda não concretizada, responsabilidade do comprador; estoque devolvido).`
          : h.action.startsWith('error') ? `O canal recusou o cancelamento automático (${h.action.slice(7)}). Cancele ${where} à mão.`
          : `Cancele ${where} à mão${o.channel === 'mercadolivre' ? ' (Full ou pedido que o Mercado Livre não deixa cancelar pela API)' : ''}.`}
      </span>
      {admin && h.action !== 'cancelled' && (
        <div><button type="button" className="btn sm" disabled={ok.isPending} onClick={() => ok.mutate()}><Check size={13} /> Já cancelei {where}</button></div>
      )}
      {ok.error && <ErrorBox error={ok.error} />}
    </div>
  );
}

/** Alerta: carrinho que ganhou pedido depois que o ERP recebeu. Lista o que falta no ERP e permite marcar como resolvido. */
function PackGrew({ o }: { o: OrderDetail }) {
  const me = useMe();
  const admin = can(me.tenant.access, 'orders', 'write') && !me.impersonating;
  const qc = useQueryClient();
  const late = o.items.filter((i) => i.lateInPack);
  const ok = useMutation({
    mutationFn: () => api(`/orders/${o.id}/pack-grew-ok`, { method: 'POST' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['order', o.id] }); void qc.invalidateQueries({ queryKey: ['orders'] }); },
  });
  return (
    <div className="notice err small" style={{ display: 'grid', gap: 6 }}>
      <b>Este carrinho ganhou pedido depois que o ERP recebeu{o.integration.erpOrderId ? ` (pedido ${o.integration.erpOrderId} no ERP)` : ''}.</b>
      <span>
        {late.length} ite{late.length === 1 ? 'm' : 'ns'} não {late.length === 1 ? 'está' : 'estão'} no pedido do ERP
        ({int(late.reduce((a, i) => a + i.quantity, 0))} un. · {brl(late.reduce((a, i) => a + (i.unitPrice ?? 0) * i.quantity, 0))}):{' '}
        {late.map((i) => `${i.quantity}× ${[i.product?.sku ?? i.sellerSku, i.title].filter(Boolean).join(' ') || i.listingId || 'item'}`).join(' · ')}.
      </span>
      <span className="muted">Complemente o pedido no ERP antes de separar. O carrinho também voltou à fila da API com a tag pack_grew_after_ack.</span>
      {admin && (
        <div><button type="button" className="btn sm" disabled={ok.isPending} onClick={() => ok.mutate()}><Check size={13} /> Já complementei no ERP</button></div>
      )}
      {ok.error && <ErrorBox error={ok.error} />}
    </div>
  );
}

const INTEGRATION: Record<OrderDetail['integration']['status'], string> = {
  none: 'Fora da integração (pedido do histórico ou não pago)',
  pending: 'Aguardando o ERP buscar',
  acked: 'Recebido pelo ERP',
};

/** Integração com o ERP, nota fiscal e etiqueta. */
/** iFood: dados para a separação/entrega e as ações do pedido (a situação muda quando o evento do iFood chega). */
/**
 * Cancelar pela loja no Mercado Livre. Entrega rápida (Proximity): pelo envio. Demais: venda não concretizada — o
 * comprador recebe o dinheiro de volta e conta como cancelamento na reputação.
 */
function MeliCancel({ o }: { o: OrderDetail }) {
  const qc = useQueryClient();
  const c = o.meliCancel!;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const cancel = useMutation({
    mutationFn: () => api(`/orders/${o.id}/meli/cancel`, { method: 'POST', json: { reason, ...(c.mode === 'feedback' && message.trim() ? { message: message.trim() } : {}) } }),
    onSuccess: () => { setOpen(false); qc.invalidateQueries({ queryKey: ['order', o.id] }); qc.invalidateQueries({ queryKey: ['orders'] }); },
  });
  return (
    <section className="panel" style={{ display: 'grid', gap: 8, boxShadow: 'none' }}>
      {!open ? (
        <div>
          <button className="btn sm danger" type="button" onClick={() => setOpen(true)}>Cancelar pedido</button>
          <span className="small muted"> {c.mode === 'proximity' ? 'Entrega rápida: a loja pode cancelar antes da entrega.' : 'Antes do envio. O comprador recebe o dinheiro de volta.'}</span>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {c.mode === 'feedback' && (
            <div className="notice warn small">
              O Mercado Livre registra a venda como <b>não concretizada pela loja</b>: o comprador recebe o dinheiro de volta e o
              cancelamento <b>conta na reputação</b> (igual a cancelar pelo painel). Use para falta de estoque, comprador que desistiu ou risco de fraude.
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <select className="select" style={{ maxWidth: 360 }} value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Motivo do cancelamento" autoFocus>
              <option value="">Motivo do cancelamento…</option>
              {c.reasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            {c.mode === 'feedback' && (
              <input className="input" style={{ flex: 1, minWidth: 220 }} maxLength={159} placeholder="Mensagem ao comprador (opcional)" value={message} onChange={(e) => setMessage(e.target.value)} />
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn sm danger" type="button" disabled={!reason || cancel.isPending} onClick={() => cancel.mutate()}>
              {cancel.isPending ? 'Cancelando…' : 'Confirmar cancelamento'}
            </button>
            <button className="btn sm quiet" type="button" onClick={() => setOpen(false)}>Voltar</button>
          </div>
        </div>
      )}
      {cancel.error && <ErrorBox error={cancel.error} />}
    </section>
  );
}

function IfoodPanel({ o }: { o: OrderDetail }) {
  const qc = useQueryClient();
  const f = o.ifood;
  const st = o.channelStatus ?? '';
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const act = useMutation({
    mutationFn: (v: { action: string; code?: string; reason?: string }) => api(`/orders/${o.id}/ifood/action`, { method: 'POST', json: v }),
    onSuccess: () => { setCancelling(false); setTimeout(() => { qc.invalidateQueries({ queryKey: ['order', o.id] }); qc.invalidateQueries({ queryKey: ['orders'] }); }, 3000); },
  });
  const reasons = useQuery({
    queryKey: ['ifood-cancel', o.id],
    queryFn: () => api<{ cancelCodeId: string; description: string }[]>(`/orders/${o.id}/ifood/cancellation-reasons`),
    enabled: cancelling,
  });
  const done = ['CONCLUDED', 'CANCELLED'].includes(st);
  const takeout = f?.orderType === 'TAKEOUT';
  const own = o.logisticType === 'own_delivery';
  return (
    <section className="panel" style={{ display: 'grid', gap: 10, boxShadow: 'none' }}>
      <h3 style={{ margin: 0, fontSize: 15 }}>iFood</h3>
      <dl className="facts">
        <div><dt>Pedido nº (cliente)</dt><dd><b>{f?.displayId ?? '—'}</b>{f?.scheduled && <> <Tag tone="info">agendado</Tag></>}</dd></div>
        <div><dt>Entrega</dt><dd>{o.shippingService ?? '—'}{o.estimatedDelivery && ` · até ${dateTime(o.estimatedDelivery)}`}</dd></div>
        <div><dt>Código de coleta</dt><dd>{f?.pickupCode ? <b className="code">{f.pickupCode}</b> : '—'}</dd></div>
        <div><dt>Telefone do cliente</dt><dd>{f?.phone ?? '—'}{f?.localizer && ` · localizador ${f.localizer}`}</dd></div>
        <div><dt>Pagamento</dt><dd>
          {f?.payments.length ? f.payments.map((p, i) => <div key={i}>{[p.method, p.type === 'OFFLINE' ? 'na entrega' : 'pago no app'].filter(Boolean).join(' · ')}{p.value != null && ` · ${brl(p.value)}`}</div>) : '—'}
          {f && f.paymentPending > 0 && <div style={{ color: 'var(--warn)' }}>A receber na entrega: {brl(f.paymentPending)}</div>}
        </dd></div>
        <div><dt>ERP</dt><dd>{o.cnpjHold ? 'Fora do ERP (pedido para CNPJ)' : INTEGRATION[o.integration.status]}{o.integration.erpOrderId && ` · pedido ${o.integration.erpOrderId}`}</dd></div>
      </dl>
      {f && f.observations.length > 0 && (
        <div className="notice warn small"><b>Observações:</b> {f.observations.join(' · ')}</div>
      )}
      {!done && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {st === 'PLACED' && <button className="btn sm primary" type="button" disabled={act.isPending} onClick={() => act.mutate({ action: 'confirm' })}>Confirmar pedido</button>}
          {['CONFIRMED', 'SEPARATION_STARTED', 'SEPARATION_ENDED'].includes(st) && (takeout || !own) && (
            <button className="btn sm primary" type="button" disabled={act.isPending} onClick={() => act.mutate({ action: 'readyToPickup' })}>{takeout ? 'Pronto para o cliente retirar' : 'Pronto para o entregador'}</button>
          )}
          {['CONFIRMED', 'SEPARATION_STARTED', 'SEPARATION_ENDED', 'READY_TO_PICKUP'].includes(st) && own && (
            <button className="btn sm primary" type="button" disabled={act.isPending} onClick={() => act.mutate({ action: 'dispatch' })}>Saiu para entrega</button>
          )}
          <button className="btn sm quiet" type="button" onClick={() => setCancelling((v) => !v)}>Cancelar pedido</button>
        </div>
      )}
      {cancelling && (
        <div className="notice" style={{ display: 'grid', gap: 8 }}>
          {reasons.isPending ? <Loading /> : reasons.error ? <ErrorBox error={reasons.error} /> : (
            <select className="select" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Motivo do cancelamento">
              <option value="">Motivo do cancelamento…</option>
              {reasons.data?.map((r) => <option key={r.cancelCodeId} value={r.cancelCodeId}>{r.description}</option>)}
            </select>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn sm" style={{ color: "var(--err)", borderColor: "var(--err)" }} type="button" disabled={!reason || act.isPending}
              onClick={() => act.mutate({ action: 'cancel', code: reason, reason: reasons.data?.find((r) => r.cancelCodeId === reason)?.description ?? 'Cancelado pela loja' })}>Confirmar cancelamento</button>
            <button className="btn sm quiet" type="button" onClick={() => setCancelling(false)}>Voltar</button>
          </div>
        </div>
      )}
      {act.isSuccess && <div className="notice small">Enviado ao iFood. A situação atualiza quando o iFood confirmar (em até 30 segundos).</div>}
      {act.error && <ErrorBox error={act.error} />}
    </section>
  );
}

function Fulfillment({ o }: { o: OrderDetail }) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const retry = useMutation({
    mutationFn: () => api<{ status: string; error: string | null }>(`/orders/${o.id}/invoice/retry`, { method: 'POST', json: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order', o.id] }),
  });
  // Um campo só: link do XML (padrão) ou a chave da nota. Ou anexar o arquivo XML.
  const [key, setKey] = useState('');
  const invoice = useMutation({
    mutationFn: (json: { xmlUrl?: string; accessKey?: string; xml?: string }) =>
      api<{ status: string; message?: string }>(`/orders/${o.id}/invoice`, { method: 'POST', json }),
    onSuccess: () => { setKey(''); qc.invalidateQueries({ queryKey: ['order', o.id] }); qc.invalidateQueries({ queryKey: ['orders'] }); },
  });
  const typed = key.trim();
  const isLink = /^https?:\/\//i.test(typed);
  const keyDigits = typed.replace(/\D/g, '');
  const canSend = isLink || keyDigits.length === 44;
  const sendTyped = () => { if (canSend) invoice.mutate(isLink ? { xmlUrl: typed } : { accessKey: keyDigits }); };
  const sendFile = async (f: File | undefined) => { if (f) invoice.mutate({ xml: await f.text() }); };
  const [labelInfo, setLabelInfo] = useState(false);
  const openLabel = async (format: 'pdf' | 'zpl') => {
    setErr(null);
    const res = await fetch(`/api/v1/orders/${o.id}/label?format=${format}`, { credentials: 'include' });
    // 202: etiqueta da Shopee ainda sendo gerada (mostra a etapa); erro: o motivo.
    if (!res.ok || res.status === 202) {
      setErr(((await res.json().catch(() => null)) as { message?: string } | null)?.message ?? 'Etiqueta indisponível.');
      return;
    }
    const url = URL.createObjectURL(await res.blob());
    if (format === 'pdf') window.open(url, '_blank');
    else {
      // ZPL: arquivo para a impressora térmica (o Mercado Livre pode mandar compactado em .zip).
      const a = document.createElement('a');
      a.href = url;
      a.download = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? `etiqueta-${o.externalId}.zpl`;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    qc.invalidateQueries({ queryKey: ['order', o.id] });
  };
  return (
    <section className="panel" style={{ display: 'grid', gap: 10, boxShadow: 'none' }}>
      <h3 style={{ margin: 0, fontSize: 15 }}>Integração, nota e etiqueta</h3>
      <dl className="facts">
        <div><dt>ERP</dt><dd>{o.cnpjHold ? 'Fora do ERP (pedido para CNPJ)' : INTEGRATION[o.integration.status]}{o.integration.erpOrderId && ` · pedido ${o.integration.erpOrderId}`}</dd></div>
        <div><dt>Recebido em</dt><dd>{o.integration.integratedAt ? dateTime(o.integration.integratedAt) : '—'}</dd></div>
        <div><dt>Comprador</dt><dd>{o.customer ? <>{o.customer.name ?? '—'}{o.customer.docMasked && ` · ${o.customer.docType ?? 'doc.'} ${o.customer.docMasked}`}</> : '—'}</dd></div>
        <div><dt>Entrega</dt><dd>{o.customer?.city ? `${o.customer.city}/${o.customer.state ?? ''}${o.customer.zip ? ` · CEP ${o.customer.zip}` : ''}${o.customer.ibge ? ` · IBGE ${o.customer.ibge}` : ''}` : '—'}</dd></div>
        <div>
          <dt>Nota fiscal</dt>
          <dd>
            {!o.invoice ? '—' : (
              <>
                {o.invoice.number ? `nº ${o.invoice.number}${o.invoice.series ? ` série ${o.invoice.series}` : ''} · ` : ''}
                {o.invoice.status === 'sent' ? <Tag tone="ok">enviada ao canal</Tag> : o.invoice.status === 'error' ? <Tag tone="err">erro no envio</Tag> : <Tag tone="off">recebida</Tag>}
                <div className="small muted code" style={{ overflowWrap: 'anywhere' }}>{o.invoice.accessKey}</div>
                {o.invoice.xmlUrl && <div className="small muted" style={{ overflowWrap: 'anywhere' }}>XML: <a href={o.invoice.xmlUrl} target="_blank" rel="noreferrer">{o.invoice.xmlUrl}</a></div>}
              </>
            )}
          </dd>
        </div>
        <div><dt>Etiqueta</dt><dd>{o.labelDownloadedAt ? `baixada em ${dateTime(o.labelDownloadedAt)}` : o.labelAvailable ? 'disponível' : '—'}</dd></div>
      </dl>
      {o.invoice?.status === 'error' && (
        <div className="error small" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{o.invoice.hasXml ? 'O canal recusou a nota' : 'Não consegui pegar o XML da nota'}: {o.invoice.error}</span>
          <span style={{ flex: 1 }} />
          {o.invoice.hasXml
            ? <button className="btn sm" type="button" disabled={retry.isPending} onClick={() => retry.mutate()}>Reenviar nota</button>
            : o.invoice.xmlUrl && <button className="btn sm" type="button" disabled={invoice.isPending} onClick={() => invoice.mutate({ xmlUrl: o.invoice!.xmlUrl!, accessKey: o.invoice!.accessKey })}>Tentar o link de novo</button>}
        </div>
      )}
      {retry.error && <ErrorBox error={retry.error} />}
      {o.invoice?.status !== 'sent' && o.status !== 'cancelled' && (
        <form style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }} onSubmit={(e) => { e.preventDefault(); sendTyped(); }}>
          <input className="input" placeholder="Link do XML da nota (ou a chave de 44 dígitos)" value={key} onChange={(e) => setKey(e.target.value)} style={{ flex: '1 1 320px' }} aria-label="Link do XML da nota" />
          <button className="btn sm" type="submit" disabled={!canSend || invoice.isPending}>{invoice.isPending ? 'Conferindo…' : 'Enviar nota ao canal'}</button>
          <label className="btn sm ghost" style={{ cursor: 'pointer' }}>
            Anexar XML
            <input type="file" accept=".xml,text/xml,application/xml" hidden onChange={(e) => { void sendFile(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
          <span className="small muted" style={{ flexBasis: '100%' }}>
            Cole o link do XML: a Berytus baixa, confere se é a NF-e deste pedido e já envia ao canal. Só a chave funciona se o link do ERP estiver em Configuração → Empresa.
          </span>
        </form>
      )}
      {invoice.data && invoice.data.status !== 'sent' && invoice.data.message && <div className="notice small">{invoice.data.message}</div>}
      {invoice.error && <ErrorBox error={invoice.error} />}
      {(o.labelAvailable || (o.channel === 'shopee' && o.logisticType !== 'fulfillment')) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {o.labelAvailable && <>
            <button className="btn sm primary" type="button" onClick={() => openLabel('pdf')}><Printer size={14} /> Imprimir etiqueta</button>
            <button className="btn sm" type="button" onClick={() => openLabel('zpl')}>Baixar ZPL (térmica)</button>
          </>}
          {o.channel === 'shopee' && o.logisticType !== 'fulfillment' && (
            <button className="btn sm" type="button" onClick={() => setLabelInfo(true)}><ListChecks size={14} /> Situação da etiqueta</button>
          )}
          {o.channel === 'shopee' && o.labelAvailable && <span className="small muted" style={{ alignSelf: 'center' }}>Shopee: se o envio não estiver organizado, a Berytus organiza a coleta ao gerar a etiqueta.</span>}
        </div>
      )}
      {labelInfo && <ShopeeLabelModal orderId={o.id} onClose={() => setLabelInfo(false)} />}
      {err && <div className="error small">{err}</div>}
    </section>
  );
}

/** Extrato do canal no pedido: as linhas como o canal informou (comissão, cada taxa, frete, descontos), líquido e repasse. */
function FinancePanel({ f }: { f: NonNullable<OrderDetail['finance']> }) {
  return (
    <details className="panel" style={{ padding: 12 }} open>
      <summary style={{ cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <b>Taxas e repasse</b>
        <span className="small muted">comissão {brl(f.commission)} · taxas {brl(f.serviceFee)} · líquido {brl(f.net)}</span>
        <Tag tone={f.releasedAt ? 'ok' : f.final ? 'info' : 'off'}>{f.releasedAt ? `Repassado ${new Date(f.releasedAt).toLocaleDateString('pt-BR')}` : f.final ? 'Definitivo' : 'Previsto'}</Tag>
      </summary>
      <table className="simple" style={{ marginTop: 8 }}>
        <tbody>
          {f.lines.map((l, i) => (
            <tr key={i} style={l.group === 'net' ? { fontWeight: 600 } : undefined}>
              <td>{l.label}{l.group === 'channel_discount' && <span className="small muted"> · pago pelo canal</span>}</td>
              <td className="num" style={{ color: l.amount < 0 && l.group !== 'channel_discount' ? 'var(--err)' : undefined }}>{brl(l.amount)}</td>
            </tr>
          ))}
          {f.payoutAmount != null && (
            <tr><td>Repassado pelo canal</td><td className="num"><b>{brl(f.payoutAmount)}</b>{Math.abs((f.payoutAmount ?? 0) - (f.net ?? 0)) >= 0.01 && <span className="small" style={{ color: 'var(--err)' }}> ({brl((f.payoutAmount ?? 0) - (f.net ?? 0))})</span>}</td></tr>
          )}
        </tbody>
      </table>
    </details>
  );
}

interface LabelInfo {
  channelStatus: string | null;
  label: null | {
    status: string; description: string; attempts: number; last_error: string | null; error_step: string | null; request_id: string | null;
    tracking_number: string | null; document_type: string | null; next_attempt_at: string | null; expires_at: string | null;
    steps: Record<string, string | null>;
  };
  log: { at: string; step: string; requestId: string | null; response: string | null }[];
}
const LABEL_STEPS: { key: string; at: string; label: string }[] = [
  { key: 'aguardando_tracking', at: 'arranged_at', label: 'Envio organizado (entrou na fila)' },
  { key: 'tracking_ok', at: 'tracking_at', label: 'Rastreio gerado pela Shopee' },
  { key: 'documento_solicitado', at: 'requested_at', label: 'Etiqueta pedida à Shopee' },
  { key: 'documento_pronto', at: 'ready_at', label: 'Etiqueta pronta na Shopee' },
  { key: 'baixado', at: 'downloaded_at', label: 'Etiqueta baixada' },
  { key: 'zpl_ok', at: 'converted_at', label: 'ZPL pronto para imprimir' },
];
const ORDER_OF = Object.fromEntries(LABEL_STEPS.map((s, i) => [s.key, i]));
const ACTIVE_LABEL = ['aguardando_tracking', 'tracking_ok', 'documento_solicitado', 'documento_pronto', 'baixado'];
const LABEL_ERROR_STEP: Record<string, string> = {
  aguardando_tracking: 'rastreio', tracking_ok: 'pedido da etiqueta', documento_solicitado: 'geração na Shopee', documento_pronto: 'download', baixado: 'conversão para ZPL',
};
const since = (a: string | null | undefined, b: string | null | undefined) => {
  if (!a || !b) return null;
  const s = Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 1000));
  return s < 60 ? `${s} s` : s < 3600 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
};

/** Shopee: em que etapa está a etiqueta (job), com o horário e o tempo de cada etapa, tentativas e o último erro. */
function ShopeeLabelModal({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['order', orderId, 'shopee-label'],
    queryFn: () => api<LabelInfo>(`/orders/${orderId}/shopee-label`),
    refetchInterval: (query) => (ACTIVE_LABEL.includes(query.state.data?.label?.status ?? '') ? 4000 : false),
  });
  const retry = useMutation({
    mutationFn: () => api(`/orders/${orderId}/shopee-label/retry`, { method: 'POST' }),
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['order', orderId, 'shopee-label'] }), 2500),
  });
  const l = q.data?.label;
  const cur = l ? (l.status === 'erro' ? -1 : ORDER_OF[l.status] ?? (l.status === 'expirado' ? LABEL_STEPS.length : -1)) : -1;
  return (
    <Modal title="Situação da etiqueta (Shopee)" onClose={onClose}>
      {q.isPending ? <Loading /> : q.error ? <ErrorBox error={q.error} /> : !l ? (
        <p className="small muted" style={{ margin: 0 }}>
          A etiqueta ainda não entrou na fila: ela entra quando o envio é organizado na Shopee (situação no canal: {q.data?.channelStatus ?? '—'}).
          Pedir a etiqueta pelo botão "Imprimir etiqueta" ou pela API organiza a coleta, se a opção estiver ligada em Configurações do canal → Logística.
        </p>
      ) : (
        <div className="stack-y" style={{ gap: 12 }}>
          <div>
            <Tag tone={l.status === 'zpl_ok' ? 'ok' : l.status === 'erro' ? 'err' : l.status === 'cancelado' || l.status === 'expirado' ? 'off' : 'info'}>{l.description}</Tag>
            {l.tracking_number && <span className="small muted"> · rastreio {l.tracking_number}</span>}
            {l.document_type && <span className="small muted"> · {l.document_type === 'THERMAL_AIR_WAYBILL' ? 'térmica' : l.document_type === 'NORMAL_AIR_WAYBILL' ? 'A4' : l.document_type}</span>}
          </div>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
            {LABEL_STEPS.map((s, i) => {
              const at = l.steps[s.at];
              const done = !!at && (cur === -1 ? true : i <= cur);
              const now = !done && (i === cur + 1 || (cur === -1 && l.status !== 'erro' && i === 0)) && ACTIVE_LABEL.includes(l.status);
              const failed = l.status === 'erro' && !at && LABEL_STEPS.findIndex((x) => !l.steps[x.at]) === i;
              const prev = i > 0 ? l.steps[LABEL_STEPS[i - 1]!.at] : null;
              return (
                <li key={s.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ marginTop: 1, color: failed ? 'var(--err)' : done ? 'var(--ok)' : now ? 'var(--info)' : 'var(--muted)' }}>
                    {failed ? <CircleAlert size={18} /> : done ? <CircleCheck size={18} /> : now ? <LoaderCircle size={18} /> : <Circle size={18} />}
                  </span>
                  <span style={{ display: 'grid' }}>
                    <span className={done || now || failed ? '' : 'muted'}>{s.label}</span>
                    <span className="small muted">
                      {at ? <>{dateTime(at)}{i > 0 && since(prev, at) ? ` · levou ${since(prev, at)}` : ''}</> : now ? 'em andamento' : failed ? 'parou aqui' : '—'}
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
          {l.status === 'erro' && (
            <div className="notice warn small">
              <b>Erro{l.steps.error_at ? ` em ${dateTime(l.steps.error_at)}` : ''}</b>{l.last_error ? `: ${l.last_error}` : ''}
              {l.request_id && <div className="muted">request_id da Shopee: {l.request_id}</div>}
            </div>
          )}
          {ACTIVE_LABEL.includes(l.status) && (l.attempts > 0 || l.last_error) && (
            <div className="small muted">
              {l.attempts > 0 && `${l.attempts} tentativa(s) nesta etapa. `}{l.last_error}
              {l.next_attempt_at && ` Próxima tentativa: ${dateTime(l.next_attempt_at)}.`}
            </div>
          )}
          {l.status === 'zpl_ok' && l.expires_at && <div className="small muted">Arquivo guardado até {dateTime(l.expires_at)} (depois disso é baixado de novo da Shopee quando pedido).</div>}
          {l.status === 'expirado' && <div className="small muted">O arquivo passou de 24 h e foi apagado (dados do comprador). Ao imprimir de novo, a Berytus baixa da Shopee outra vez.</div>}
          {l.status === 'cancelado' && <div className="small muted">Pedido cancelado{l.steps.cancelled_at ? ` em ${dateTime(l.steps.cancelled_at)}` : ''}: saiu da fila de etiquetas.</div>}
          {(l.status === 'erro' || ACTIVE_LABEL.includes(l.status)) && (
            <div>
              <button className="btn sm" type="button" disabled={retry.isPending} onClick={() => retry.mutate()}>
                <RefreshCw size={14} /> {l.status === 'erro' ? `Tentar de novo (a partir do ${LABEL_ERROR_STEP[l.error_step ?? ''] ?? 'passo que falhou'})` : 'Processar agora'}
              </button>
            </div>
          )}
          {retry.error && <ErrorBox error={retry.error} />}
          {q.data!.log.length > 0 && (
            <details>
              <summary className="small">Últimas chamadas à Shopee ({q.data!.log.length})</summary>
              <table className="simple" style={{ marginTop: 6 }}>
                <tbody>
                  {q.data!.log.map((x, i) => (
                    <tr key={i}>
                      <td className="small" style={{ whiteSpace: 'nowrap' }}>{dateTime(x.at)}</td>
                      <td className="small code">{x.step}</td>
                      <td className="small muted" style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>{x.response}{x.requestId ? ` · ${x.requestId}` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </Modal>
  );
}
